import { InboxOutlined } from "@ant-design/icons"
import type { UploadFile } from "antd"
import { App, Form, Modal, Typography, Upload } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { createProjectDocument } from "../../Project/api/project-documents.services.ts"
import type { ProjectDocument } from "../../Project/types/project-documents.types.ts"
import { classifyFileFormat } from "../helpers/classify-file-format.ts"
import { readServerMessage } from "../helpers/server-message.ts"
import { type FileItem } from "../types/file-item.types.ts"
import { TextArea } from "../../../components/Input/TextArea.tsx"

const { Text } = Typography
interface UploadNewVersionModalProps {
  projectId: string
  file: FileItem
  onClose: () => void
  onUploaded: (newVersion: ProjectDocument) => void
}

export function UploadNewVersionModal({
  projectId,
  file,
  onClose,
  onUploaded
}: UploadNewVersionModalProps) {
  const { t } = useTranslation()
  const { message, modal } = App.useApp()
  const [selected, setSelected] = useState<UploadFile | null>(null)
  const [revisionNote, setRevisionNote] = useState("")
  const [saving, setSaving] = useState(false)

  const upload = async () => {
    const originFile = selected?.originFileObj
    if (!originFile) return
    setSaving(true)
    const response = await createProjectDocument({
      lead: projectId,
      file_name: file.name,
      file: originFile,
      previous_version_id: file.id,
      ...(revisionNote.trim() ? { revision_note: revisionNote.trim() } : {})
    })
    setSaving(false)
    if (!response.ok || !response.data) {
      message.error(readServerMessage(response.data) ?? t("files.uploadNewVersion.failed"))
      return
    }
    message.success(t("files.uploadNewVersion.succeeded"))
    onUploaded(response.data.details.data)
    onClose()
  }

  const handleSubmit = () => {
    if (!selected) return
    const currentFormat = classifyFileFormat({ name: file.name, mimeType: file.mimeType })
    const newFormat = classifyFileFormat({ name: selected.name })
    if (newFormat !== currentFormat) {
      modal.confirm({
        title: t("files.uploadNewVersion.typeMismatchTitle"),
        content: t("files.uploadNewVersion.typeMismatchBody", {
          newType: t(`files.filters.fileTypeCategories.${newFormat}`),
          currentType: t(`files.filters.fileTypeCategories.${currentFormat}`)
        }),
        okText: t("files.uploadNewVersion.continueAnyway"),
        cancelText: t("files.filters.cancel"),
        onOk: () => void upload()
      })
      return
    }
    void upload()
  }

  return (
    <Modal
      open
      title={t("files.uploadNewVersion.title")}
      okText={t("files.uploadNewVersion.submit")}
      onOk={handleSubmit}
      onCancel={onClose}
      confirmLoading={saving}
      okButtonProps={{ disabled: !selected }}
      cancelButtonProps={{ disabled: saving }}
    >
      <Form layout="vertical" requiredMark={false}>
        <Form.Item label={t("files.uploadNewVersion.currentVersion")}>
          <Text>
            {file.version !== undefined
              ? t("fileViewer.detailsPanel.versionCurrent", { version: file.version })
              : "—"}
          </Text>
        </Form.Item>

        <Form.Item
          label={t("files.uploadNewVersion.selectFile")}
          required
          validateStatus={selected ? undefined : "warning"}
        >
          <Upload.Dragger
            multiple={false}
            beforeUpload={() => false}
            fileList={selected ? [selected] : []}
            disabled={saving}
            onChange={({ fileList }) => setSelected(fileList[fileList.length - 1] ?? null)}
            onRemove={() => setSelected(null)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <Text>{t("projectDocuments.upload.dragHint")}</Text>
          </Upload.Dragger>
        </Form.Item>

        <Form.Item label={t("files.uploadNewVersion.revisionNote")}>
          <TextArea
            disabled={saving}
            value={revisionNote}
            onChange={(e) => setRevisionNote(e.target.value)}
            placeholder={t("files.uploadNewVersion.revisionNotePlaceholder")}
            rows={3}
            maxLength={500}
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}
